import { useEffect, useRef, useState } from "react";
import { rememberPlan, getRememberedPlan, clearRememberedPlan, PLAN_LABEL, type PendingPlan } from "@/lib/checkoutIntent";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { BadgeCheck, Clock, Lock, Upload, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PageContainer } from "@/components/layout/PageContainer";
import { SEO } from "@/components/seo/SEO";
import { RedactableImage } from "@/components/verification/RedactableImage";
import { startCheckout, type BillingPlan, type CheckoutResult } from "@/lib/startTrialCheckout";
import { CheckoutNotice } from "@/components/auth/CheckoutNotice";

const REGIONS = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine",
  "Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma",
  "Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

type Submission = {
  id: string; status: "pending" | "approved" | "rejected" | "needs_info";
  reviewer_notes: string | null; credential_type: string; created_at: string;
  attested_at?: string | null; license_expires_on?: string | null;
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Mirrors the server gate in create-trial-checkout. */
function isEligible(list: Submission[]) {
  if (list.some((r) => r.status === "approved")) return true;
  if (list.some((r) => r.credential_type === "license" && r.status === "rejected")) return false;
  const t = todayISO();
  return list.some((r) => r.credential_type === "license" && r.status !== "rejected" && !!r.attested_at && !!r.license_expires_on && r.license_expires_on >= t);
}

const PLANS: { plan: BillingPlan; name: string; price: string; note: string }[] = [
  { plan: "weekly", name: "Stylist Weekly", price: "$17/week", note: "$17 charged today and every week until you cancel." },
  { plan: "monthly", name: "Stylist Monthly", price: "$47/month", note: "$47 charged today and every month until you cancel." },
  { plan: "yearly", name: "Stylist Yearly", price: "$564/year", note: "$564 charged today and every year until you cancel." },
];

const schema = z.object({
  fullName: z.string().trim().min(2, "Enter your full name").max(120),
  region: z.string().trim().min(2, "Choose the issuing state or country").max(80),
  type: z.enum(["license", "other"]),
  licenseNumber: z.string().trim().max(60),
  description: z.string().trim().max(500),
  expires: z.string(),
  attest: z.boolean(),
}).superRefine((v, ctx) => {
  if (v.type === "license") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v.expires) || isNaN(Date.parse(v.expires)))
      ctx.addIssue({ code: "custom", message: "Enter your license expiration date", path: ["expires"] });
    else if (v.expires < todayISO())
      ctx.addIssue({ code: "custom", message: "This license appears to be expired. Enter a current license to continue.", path: ["expires"] });
    if (!v.attest)
      ctx.addIssue({ code: "custom", message: "Please confirm the statement to continue", path: ["attest"] });
  }
  if (v.type === "license" && v.licenseNumber.length < 2)
    ctx.addIssue({ code: "custom", message: "Enter your license number", path: ["licenseNumber"] });
  if (v.type === "other" && v.description.length < 5)
    ctx.addIssue({ code: "custom", message: "Tell us what this credential is", path: ["description"] });
});

const input = "w-full rounded-xl border border-border bg-background px-4 py-3 font-body text-sm text-foreground outline-none focus:border-primary";
const label = "font-body text-xs uppercase tracking-widest text-muted-foreground";

export default function VerifyLicenseForHPEPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [latest, setLatest] = useState<Submission | null>(null);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resubmit, setResubmit] = useState(false);

  const [fullName, setFullName] = useState("");
  const [region, setRegion] = useState("");
  const [otherCountry, setOtherCountry] = useState("");
  const [type, setType] = useState<"license" | "other">("license");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [description, setDescription] = useState("");
  const [expires, setExpires] = useState("");
  const [attest, setAttest] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState<BillingPlan | null>(null);
  const [checkout, setCheckout] = useState<{ plan: BillingPlan; result: CheckoutResult } | null>(null);
  const getBlob = useRef<(() => Promise<Blob | null>) | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const autoStarted = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/verify-license${window.location.search}`)}`, { replace: true });
      return;
    }
    (async () => {
      const [{ data: adm }, { data: rows }] = await Promise.all([
        supabase.rpc("is_hpe_admin" as any, { check_user_id: user!.id }),
        supabase.from("hpe_license_verifications" as any)
          .select("id,status,reviewer_notes,credential_type,created_at,attested_at,license_expires_on")
          .eq("user_id", user!.id).order("created_at", { ascending: false }),
      ]);
      const list = (rows ?? []) as unknown as Submission[];
      setIsAdmin(adm === true);
      setApproved(isEligible(list));
      setLatest(list[0] ?? null);
      setLoading(false);
    })();
  }, [authLoading, isAuthenticated, user, navigate]);

  const submit = async () => {
    const issuing = region === "__other" ? otherCountry : region;
    const parsed = schema.safeParse({ fullName, region: issuing, type, licenseNumber, description, expires, attest });
    if (!parsed.success) { toast.error(parsed.error.issues[0].message); return; }
    if (type === "license") {
      setSubmitting(true);
      const v = parsed.data;
      const { error } = await supabase.from("hpe_license_verifications" as any).insert({
        user_id: user!.id, full_name: v.fullName, issuing_region: v.region, credential_type: "license",
        license_number: v.licenseNumber, license_expires_on: v.expires, attested_at: new Date().toISOString(), file_path: "",
      });
      setSubmitting(false);
      if (error) { console.error(error); toast.error("We couldn't save your details. Please try again."); return; }
      toast.success("Details saved");
      setApproved(true);
      const p = PLANS.find((x) => x.plan === (params.get("plan") ?? getRememberedPlan()));
      if (p) { autoStarted.current = true; clearRememberedPlan(); choose(p.plan); }
      return;
    }
    if (!file || !getBlob.current) { toast.error("Add a clear photo or screenshot of your credential"); return; }
    setSubmitting(true);
    try {
      const blob = await getBlob.current();
      if (!blob) throw new Error("Could not prepare image");
      const path = `${user!.id}/${Date.now()}.jpg`;
      const up = await supabase.storage.from("hpe-verification-docs").upload(path, blob, { contentType: "image/jpeg" });
      if (up.error) throw up.error;
      const v = parsed.data;
      const { error } = await supabase.from("hpe_license_verifications" as any).insert({
        user_id: user!.id,
        full_name: v.fullName,
        issuing_region: v.region,
        credential_type: v.type,
        license_number: v.licenseNumber || null,
        credential_description: v.type === "other" ? v.description : null,
        file_path: path,
      });
      if (error) throw error;
      setLatest({ id: "new", status: "pending", reviewer_notes: null, credential_type: v.type, created_at: new Date().toISOString() });
      setResubmit(false);
      toast.success("Submitted for review");
    } catch (e) {
      console.error(e);
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const choose = async (plan: BillingPlan) => {
    if (starting) return;
    setStarting(plan); setCheckout(null);
    const result = await startCheckout(plan);
    setCheckout({ plan, result });
    if (result.status !== "redirected" && result.status !== "rerouted") setStarting(null);
  };

  const urlPlan = params.get("plan");
  useEffect(() => { rememberPlan(urlPlan); }, [urlPlan]);
  const preferred = urlPlan ?? getRememberedPlan();
  // Auto-continue to checkout for the plan chosen before sign-in/verification, once approved.
  useEffect(() => {
    if (loading || autoStarted.current) return;
    if (!(approved || isAdmin)) return;
    const wanted = params.get("autostart") === "1" || (!urlPlan && !!getRememberedPlan());
    if (!wanted) return;
    const p = PLANS.find((x) => x.plan === preferred);
    if (!p) return;
    autoStarted.current = true;
    clearRememberedPlan();
    choose(p.plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, approved, isAdmin, preferred]);
  const showForm = !approved && !isAdmin && (!latest || resubmit || latest.status === "rejected" || latest.status === "needs_info" || latest.credential_type === "license");

  return (
    <PageContainer className="flex flex-col items-center bg-background px-5 pt-8 pb-24">
      <SEO title="Professional Verification — HairProEncyclopedia.ai" description="Verify your cosmetology or barber license to join HairProEncyclopedia.ai." path="/verify-license" />
      <div className="w-full max-w-lg flex flex-col gap-5">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <BadgeCheck className="h-7 w-7" />
          </div>
          <h1 className="font-display text-2xl text-foreground">Professional Verification</h1>
          <p className="font-body text-sm text-muted-foreground">
            HairProEncyclopedia.ai membership is for licensed professionals. Verify once — you won't need to do it again.
          </p>
        </div>

        {loading ? (
          <p className="text-center font-body text-sm text-muted-foreground">Loading…</p>
        ) : approved || isAdmin ? (
          <div className="flex flex-col gap-4 rounded-2xl border border-primary/40 bg-card p-6">
            <div className="flex items-center gap-2 text-primary">
              <BadgeCheck className="h-5 w-5" />
              <p className="font-body text-sm font-semibold">You're all set. Choose your plan.</p>
            </div>
            {PLANS.map((p) => (
              <button key={p.plan} onClick={() => choose(p.plan)} disabled={!!starting}
                className={`flex flex-col items-start rounded-xl border p-4 text-left transition-colors hover:border-primary disabled:opacity-60 ${preferred === p.plan ? "border-primary" : "border-border"}`}>
                <span className="font-display text-lg text-foreground">{p.name} — {p.price}</span>
                <span className="font-body text-xs text-muted-foreground">{p.note}</span>
                {starting === p.plan && <span className="mt-1 font-body text-xs text-primary">Opening secure checkout…</span>}
              </button>
            ))}
            {checkout && <CheckoutNotice result={checkout.result} onRetry={() => choose(checkout.plan)} retrying={!!starting} />}
            <p className="font-body text-xs text-muted-foreground">
              Recurring subscription. HPE access starts as soon as payment is confirmed. Cancel anytime.
            </p>
          </div>
        ) : !showForm && latest ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-8 text-center">
            <Clock className="h-9 w-9 text-primary" />
            <h2 className="font-display text-xl text-foreground">Verification under review</h2>
            <p className="font-body text-sm text-muted-foreground">
              Every credential is checked by our team before membership opens. Once you're approved, come back to this page{preferred ? <> and we'll take you straight to checkout for <strong className="text-foreground">{PLAN_LABEL[preferred as PendingPlan]}</strong></> : " to choose your plan"}.
            </p>
            <p className="font-body text-xs text-muted-foreground">You can still use your free introductory question while you wait.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-6">
            {latest && (latest.status === "rejected" || latest.status === "needs_info") && (
              <div className="flex gap-2 rounded-xl border border-destructive/40 bg-destructive/10 p-3">
                <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
                <p className="font-body text-xs text-foreground">
                  {latest.status === "needs_info" ? "We need a little more information." : "We couldn't verify your last submission."}
                  {latest.reviewer_notes ? ` ${latest.reviewer_notes}` : ""} Please submit again.
                </p>
              </div>
            )}

            <div className="flex rounded-xl bg-muted p-1">
              {(["license", "other"] as const).map((t) => (
                <button key={t} type="button" onClick={() => setType(t)}
                  className={`flex-1 rounded-lg py-2.5 font-body text-xs font-medium ${type === t ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                  {t === "license" ? "Cosmetology or barber license" : "I have another credential"}
                </button>
              ))}
            </div>

            {type === "other" && (
              <p className="font-body text-xs text-muted-foreground">
                For example, proof of current cosmetology or barber school enrollment. These are reviewed personally before checkout opens.
              </p>
            )}

            <div className="flex flex-col gap-1.5">
              <label className={label}>{type === "license" ? "Name as it appears on your license" : "Full name"}</label>
              <input className={input} value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={120} placeholder="Name as shown on your credential" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className={label}>Issuing state</label>
              <select className={input} value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="" disabled>Select…</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                <option value="__other">Outside the U.S.</option>
              </select>
              {region === "__other" && (
                <input className={input} value={otherCountry} onChange={(e) => setOtherCountry(e.target.value)} maxLength={80} placeholder="Country (and province/region)" />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className={label}>{type === "license" ? "License number" : "Student ID or license number (if any)"}</label>
              <input className={input} value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} maxLength={60} />
            </div>

            {type === "other" && (
              <div className="flex flex-col gap-1.5">
                <label className={label}>What is this credential?</label>
                <textarea className={input} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
                  placeholder="e.g. Enrolled at ABC Beauty Academy, expected graduation May 2027" />
              </div>
            )}

            {type === "license" && (
              <>
                <div className="flex flex-col gap-1.5">
                  <label className={label}>Expiration date</label>
                  <input type="date" className={input} value={expires} min={todayISO()} onChange={(e) => setExpires(e.target.value)} />
                </div>
                <label className="flex items-start gap-3 font-body text-sm text-foreground">
                  <input type="checkbox" className="mt-1 h-4 w-4 accent-primary" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
                  <span>I confirm that these details are accurate and that I hold a current cosmetology or barber license.</span>
                </label>
              </>
            )}

            {type === "other" && (
            <div className="flex flex-col gap-1.5">
              <label className={label}>Photo or screenshot</label>
              <button type="button" onClick={() => fileInput.current?.click()}
                className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left font-body text-sm text-muted-foreground hover:border-primary">
                <Upload className="h-4 w-4 text-primary" />
                {file ? file.name : "Choose a clear photo or screenshot…"}
              </button>
              <input ref={fileInput} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0] ?? null; if (f && f.size > 15 * 1024 * 1024) { toast.error("Image is too large"); return; } setFile(f); }} />
              {file && <RedactableImage file={file} onReady={(fn) => { getBlob.current = fn; }} />}
            </div>
            )}

            <button type="button" onClick={submit} disabled={submitting}
              className="w-full rounded-xl bg-primary py-4 font-body text-base font-semibold text-primary-foreground disabled:opacity-50">
              {submitting ? "Saving…" : type === "license" ? "Continue to checkout" : "Submit for review"}
            </button>

            <div className="flex items-start gap-2">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <p className="font-body text-xs text-muted-foreground">
                {type === "license"
                  ? "Your details are stored privately and seen only by the HairProEncyclopedia.ai owner, who may review them. They are self-reported and not independently verified at signup."
                  : "Your image is stored privately and seen only by our reviewers. Student and other credentials are reviewed personally before membership opens."}
              </p>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
